import { useState } from "react";
import { useAuth, type Rol } from "../auth/authContext.ts";
import {
  apiConfigurada,
  enrolarTotp,
  iniciarSesion,
  loginPasoMfa,
  verificarEnrolamientoTotp,
  type ApiSession,
  type EnrolamientoTotp,
} from "../lib/api.ts";
import { C, SANS, SERIF, st } from "../ui/theme.ts";
import { LogoArgus } from "../ui/iconos.tsx";

const OPACIDAD_CARGANDO = 0.7;

/** Paso del flujo de login real, más allá de "captura email/password". */
type PasoLogin =
  | { tipo: "credenciales" }
  | { tipo: "recomendar_mfa"; session: ApiSession; accessToken: string; factorId: string }
  | { tipo: "recomendar_enrolamiento"; session: ApiSession; accessToken: string }
  | { tipo: "enrolamiento"; session: ApiSession; accessToken: string; enrolamiento: EnrolamientoTotp };

function sesionDeApi(s: ApiSession, nombre?: string): Parameters<ReturnType<typeof useAuth>["setSession"]>[0] {
  const session: NonNullable<Parameters<ReturnType<typeof useAuth>["setSession"]>[0]> = {
    userId: s.userId,
    tenantId: s.tenantId,
    rol: s.rol,
    email: s.email,
    nombre: nombre ?? s.email,
    accessToken: s.accessToken,
    aal: s.aal,
  };
  if (s.refreshToken) session.refreshToken = s.refreshToken;
  if (s.expiresAt) session.expiresAt = s.expiresAt;
  return session;
}

function qrTotpSrc(qrCode?: string): string | null {
  if (!qrCode) return null;
  if (qrCode.startsWith("data:")) return qrCode;
  return `data:image/svg+xml;utf8,${encodeURIComponent(qrCode)}`;
}

/**
 * Acceso. Si hay backend configurado (VITE_SUPABASE_URL) el submit hace login
 * REAL: GoTrue password grant → rol desde la tabla usuario. MFA/TOTP es
 * recomendado y puede elevar la sesion a aal2, pero no bloquea el acceso al
 * producto. Los botones de abajo entran en MODO
 * DEMOSTRACIÓN local (sin backend, datos en memoria, MFA no aplica).
 */
export function LoginPage() {
  const { authError, setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [codigoMfa, setCodigoMfa] = useState("");
  const [paso, setPaso] = useState<PasoLogin>({ tipo: "credenciales" });
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);

  const entrarDemo = (rol: Rol) => {
    setSession({ userId: "demo", tenantId: "demo", rol, nombre: email || "Usuario demo", accessToken: "", aal: "demo" });
  };

  const enviarCredenciales = async () => {
    setError(null);
    setCargando(true);
    try {
      const resultado = await iniciarSesion(email, password);
      setCodigoMfa("");
      if (resultado.estado === "ok") {
        setSession(sesionDeApi(resultado.session));
      } else if (resultado.estado === "recomienda_mfa") {
        setPaso({
          tipo: "recomendar_mfa",
          session: resultado.session,
          accessToken: resultado.accessToken,
          factorId: resultado.factorId,
        });
      } else {
        setPaso({ tipo: "recomendar_enrolamiento", session: resultado.session, accessToken: resultado.accessToken });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo iniciar sesión.");
    } finally {
      setCargando(false);
    }
  };

  const confirmarMfa = async () => {
    if (paso.tipo !== "recomendar_mfa") return;
    setError(null);
    setCargando(true);
    try {
      const s = await loginPasoMfa(paso.accessToken, paso.factorId, codigoMfa, email);
      setSession(sesionDeApi(s));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Código MFA inválido.");
    } finally {
      setCargando(false);
    }
  };

  const iniciarEnrolamiento = async () => {
    if (paso.tipo !== "recomendar_enrolamiento") return;
    setError(null);
    setCargando(true);
    try {
      const enrolamiento = await enrolarTotp(paso.accessToken);
      setCodigoMfa("");
      setPaso({ tipo: "enrolamiento", session: paso.session, accessToken: paso.accessToken, enrolamiento });
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo preparar el código QR.");
    } finally {
      setCargando(false);
    }
  };

  const confirmarEnrolamiento = async () => {
    if (paso.tipo !== "enrolamiento") return;
    setError(null);
    setCargando(true);
    try {
      const s = await verificarEnrolamientoTotp(paso.accessToken, paso.enrolamiento.factorId, codigoMfa, email);
      setSession(sesionDeApi(s));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo confirmar el código del autenticador.");
    } finally {
      setCargando(false);
    }
  };

  const continuarSinMfa = () => {
    if (paso.tipo === "recomendar_mfa" || paso.tipo === "recomendar_enrolamiento" || paso.tipo === "enrolamiento") {
      setSession(sesionDeApi(paso.session));
    }
  };

  const qrEnrolamiento = paso.tipo === "enrolamiento" ? qrTotpSrc(paso.enrolamiento.qrCode) : null;

  return (
    <main
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        fontFamily: SANS,
        background: C.bg,
        color: C.ink,
        gap: "1.5rem",
        padding: "1.5rem",
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <LogoArgus alto={40} color={C.tiel} />
        <h1 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 26, letterSpacing: "0.24em", margin: 0 }}>ARGUS</h1>
        <p style={{ margin: 0, fontSize: 13, color: C.muted }}>Cumplimiento LFPIORPI para desarrollo inmobiliario</p>
      </div>

      {paso.tipo === "credenciales" && (
        <form
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.8rem",
            width: 340,
            maxWidth: "100%",
            padding: "1.5rem",
            border: `1px solid ${C.line}`,
            borderRadius: 14,
            background: "#fff",
            boxSizing: "border-box",
          }}
          onSubmit={(e) => {
            e.preventDefault();
            if (apiConfigurada()) {
              void enviarCredenciales();
            } else {
              entrarDemo("rec");
            }
          }}
        >
          <div>
            <label htmlFor="login-email" style={st.label}>Correo</label>
            <input
              id="login-email"
              style={st.input}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => { setEmail(e.target.value); }}
            />
          </div>
          <div>
            <label htmlFor="login-password" style={st.label}>Contraseña</label>
            <input
              id="login-password"
              style={st.input}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); }}
            />
          </div>
          <button
            type="submit"
            disabled={cargando}
            style={{ ...st.btnPrim, opacity: cargando ? OPACIDAD_CARGANDO : 1 }}
          >
            {cargando ? "Entrando…" : "Entrar"}
          </button>
          {(error || authError) && <span style={{ fontSize: 13, color: C.red }}>{error ?? authError}</span>}
          {!apiConfigurada() && (
            <span style={{ fontSize: 12, color: C.muted }}>
              Sin backend configurado: el submit entra en modo demostración.
            </span>
          )}
        </form>
      )}

      {paso.tipo === "recomendar_mfa" && (
        <form
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.8rem",
            width: 340,
            maxWidth: "100%",
            padding: "1.5rem",
            border: `1px solid ${C.line}`,
            borderRadius: 14,
            background: "#fff",
            boxSizing: "border-box",
          }}
          onSubmit={(e) => {
            e.preventDefault();
            void confirmarMfa();
          }}
        >
          <p style={{ margin: 0, fontSize: 13, color: C.muted }}>
            Tu cuenta ya tiene MFA configurado. Puedes verificarlo ahora o continuar.
          </p>
          <div>
            <label htmlFor="login-mfa" style={st.label}>Código MFA</label>
            <input
              id="login-mfa"
              style={st.input}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={codigoMfa}
              onChange={(e) => { setCodigoMfa(e.target.value); }}
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              disabled={cargando}
              style={{ ...st.btn, flex: 1, opacity: cargando ? OPACIDAD_CARGANDO : 1 }}
              onClick={continuarSinMfa}
            >
              Continuar
            </button>
            <button
              type="submit"
              disabled={cargando}
              style={{ ...st.btnPrim, flex: 1, opacity: cargando ? OPACIDAD_CARGANDO : 1 }}
            >
              {cargando ? "Verificando…" : "Verificar"}
            </button>
          </div>
          {error && <span style={{ fontSize: 13, color: C.red }}>{error}</span>}
        </form>
      )}

      {paso.tipo === "recomendar_enrolamiento" && (
        <section
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.8rem",
            width: 360,
            maxWidth: "100%",
            padding: "1.5rem",
            border: `1px solid ${C.line}`,
            borderRadius: 14,
            background: "#fff",
            boxSizing: "border-box",
          }}
        >
          <p style={{ margin: 0, fontSize: 13, color: C.ink }}>MFA recomendado</p>
          <p style={{ margin: 0, fontSize: 12, color: C.muted }}>
            Puedes entrar ahora. Para mejorar la seguridad de la cuenta, configura un autenticador con código QR.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              disabled={cargando}
              style={{ ...st.btn, flex: 1, opacity: cargando ? OPACIDAD_CARGANDO : 1 }}
              onClick={continuarSinMfa}
            >
              Entrar ahora
            </button>
            <button
              type="button"
              disabled={cargando}
              style={{ ...st.btnPrim, flex: 1, opacity: cargando ? OPACIDAD_CARGANDO : 1 }}
              onClick={() => { void iniciarEnrolamiento(); }}
            >
              {cargando ? "Preparando…" : "Configurar QR"}
            </button>
          </div>
          {error && <span style={{ fontSize: 13, color: C.red }}>{error}</span>}
        </section>
      )}

      {paso.tipo === "enrolamiento" && (
        <form
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.8rem",
            width: 380,
            maxWidth: "100%",
            padding: "1.5rem",
            border: `1px solid ${C.line}`,
            borderRadius: 14,
            background: "#fff",
            boxSizing: "border-box",
          }}
          onSubmit={(e) => {
            e.preventDefault();
            void confirmarEnrolamiento();
          }}
        >
          <p style={{ margin: 0, fontSize: 13, color: C.ink }}>
            Escanea el código QR con Microsoft Authenticator, Google Authenticator o 1Password.
          </p>
          <p style={{ margin: 0, fontSize: 12, color: C.muted }}>
            Después de escanearlo, escribe aquí el código de 6 dígitos y presiona Activar MFA.
          </p>
          {qrEnrolamiento ? (
            <img
              src={qrEnrolamiento}
              alt="Código QR para MFA"
              style={{ width: 190, height: 190, alignSelf: "center", border: `1px solid ${C.line}`, borderRadius: 8 }}
            />
          ) : (
            <span style={{ fontSize: 12, color: C.muted }}>El proveedor no devolvió imagen QR; usa la clave manual.</span>
          )}
          <details style={{ fontSize: 12, color: C.muted }}>
            <summary>Usar clave manual</summary>
            <code style={{ display: "block", marginTop: 8, fontSize: 11, wordBreak: "break-all", padding: "0.5rem", background: C.bg, borderRadius: 8 }}>
              {paso.enrolamiento.secreto}
            </code>
          </details>
          <div>
            <label htmlFor="login-enrolamiento" style={st.label}>Código de confirmación</label>
            <input
              id="login-enrolamiento"
              style={st.input}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={codigoMfa}
              onChange={(e) => { setCodigoMfa(e.target.value); }}
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              disabled={cargando}
              style={{ ...st.btn, flex: 1, opacity: cargando ? OPACIDAD_CARGANDO : 1 }}
              onClick={continuarSinMfa}
            >
              Omitir
            </button>
            <button
              type="submit"
              disabled={cargando}
              style={{ ...st.btnPrim, flex: 1, opacity: cargando ? OPACIDAD_CARGANDO : 1 }}
            >
              {cargando ? "Confirmando…" : "Activar MFA"}
            </button>
          </div>
          {error && <span style={{ fontSize: 13, color: C.red }}>{error}</span>}
        </form>
      )}

      {paso.tipo === "credenciales" && (
        <div style={{ textAlign: "center", color: C.muted, fontSize: 13 }}>
          <p style={{ margin: "0 0 0.5rem" }}>Modo demostración — entrar como:</p>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button style={st.btn} onClick={() => { entrarDemo("ventas"); }}>
              Ventas
            </button>
            <button style={st.btn} onClick={() => { entrarDemo("rec"); }}>
              Cumplimiento (REC)
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
